import React, { useState, useEffect } from 'react'
import Image from "next/image"
import Link from "next/link"
import { Carousel, CarouselApi, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel"
import SliderIndicator from '../ui/slider-indicator'
import ImageWithFallback from '../ui/image'
import { absoluteUrl } from '@/lib/utils'

interface NodePodcastPromotedProps {
  node: any
  title?: string
}

export function NodePodcastHistorySlider({ node, title, ...props }: NodePodcastPromotedProps) {
  const [api, setApi] = useState<CarouselApi>()
  const [isStart, setIsStart] = useState(true)
  const [isEnd, setIsEnd] = useState(false)
  const [imageError, setImageError] = useState<{ [key: string]: boolean }>({})

  useEffect(() => {
    if (!api) {
      return
    }

    api.on("select", () => {
      if (!api) return
      setIsStart(!api.canScrollPrev())
      setIsEnd(!api.canScrollNext())
    })
  }, [api])

  const convertTime = (time: number) => {
    const hours = Math.floor(time / 3600)
    const minutes = Math.floor((time % 3600) / 60)
    const seconds = time % 60
    return `${hours > 0 ? hours + ' h' : ''} ${minutes > 0 ? minutes + ' min' : ''} ${seconds > 0 ? seconds + ' s' : ''} écoutées`
  }

  const handleImageError = (index: number) => {
    setImageError(prev => ({
      ...prev,
      [index]: true
    }))
  }

  return (
    <Carousel
      setApi={setApi}
      className=''
      opts={{
        align: "start",
        dragFree: true,
      }}
    >
      <div className="w-full overflow-hidden">
        {!isStart &&
          <div className="h-full absolute flex items-center z-10 left-16 2xl:left-52 bottom-5 scale-125 max-md:hidden">
            <CarouselPrevious />
          </div>
        }
        {!isEnd &&
          <div className="h-full absolute flex items-center z-10 right-32 bottom-5 scale-125 max-md:hidden">
            <CarouselNext />
          </div>
        }
        <div className="p-slider-1 z-0">
          <CarouselContent overflowHidden={false}>
            <CarouselItem className="md:basis-[5%] lg:basis-[2%] mx-0 scale-125 max-md:basis-[0%] max-md:invisible">
              {isStart &&
                <CarouselPrevious />
              }
            </CarouselItem>
            {node?.map((item, index) => (
              <CarouselItem key={index} className="basis-4-5/5 md:basis-2/5 lg:basis-[30%] bg-[#F7F7F7] mx-2 rounded-lg">
                <div className="flex gap-4">
                  <Link href={item.podcast.path?.alias || '#'} className="flex-none py-2.5">
                    <figure className="relative">
                      {(item.podcast.field_media_illustration &&
                        <ImageWithFallback
                          className="rounded-lg w-20 h-20 object-cover object-center"
                          // src={"/rrb-1985.png"}
                          src={absoluteUrl(item.podcast.field_media_illustration.field_media_image.uri.url)}
                          width={128}
                          height={128}
                          alt={item.podcast.field_media_illustration?.field_media_image?.resourceIdObjMeta?.alt || ''}
                          priority

                        />
                      )}
                    </figure>
                  </Link>

                  <div className="flex-1 min-w-0 flex flex-col justify-between py-4 pr-2">
                    <Link href={item.podcast.path?.alias || '#'}>
                      <h4 className="font-medium text-foreground-dark line-clamp-1">
                        {item.podcast.field_emission?.title || 'Untitled Podcast'}
                      </h4>
                    </Link>
                    {item.podcast?.field_animateurs?.length > 0 &&
                      <p className="text-sm font-light text-[#99A0AF] text-foreground-dark line-clamp-1 select-none">
                        {item.podcast?.field_animateurs.map((animator, i) => (
                          <React.Fragment key={i}>
                            {i > 0 && ", "}
                            {animator.name}
                          </React.Fragment>
                        ))}
                      </p>
                    }
                    {item.podcast.field_emission?.title && (
                      <p className="text-sm font-normal text-foreground-dark line-clamp-2 select-none">
                        {item.podcast.title}
                      </p>
                    )}
                    <div className='flex items-center gap-2'>
                      {item.history?.completed_at !== null && (
                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
                          <path fillRule="evenodd" clipRule="evenodd" d="M9 1.5C4.86 1.5 1.5 4.86 1.5 9C1.5 13.14 4.86 16.5 9 16.5C13.14 16.5 16.5 13.14 16.5 9C16.5 4.86 13.14 1.5 9 1.5ZM6.9675 12.2175L4.275 9.525C3.9825 9.2325 3.9825 8.76 4.275 8.4675C4.5675 8.175 5.04 8.175 5.3325 8.4675L7.5 10.6275L12.66 5.4675C12.9525 5.175 13.425 5.175 13.7175 5.4675C14.01 5.76 14.01 6.2325 13.7175 6.525L8.025 12.2175C7.74 12.51 7.26 12.51 6.9675 12.2175Z" fill="#0F73CF" />
                        </svg>
                      )}
                      <span className='flex-1 text-[#99A0AF] text-sm select-none'>
                        {item.history?.completed_at === null ? convertTime(item.history?.seconds_elapsed || 0) : 'Terminé'}
                      </span>
                    </div>
                  </div>
                </div>
              </CarouselItem>
            ))}
            <CarouselItem className="basis-4-5/5 md:basis-2/5 lg:basis-[30%]">
              <div className="rounded-xl text-white bg-podcast-illustration bg-primary p-[18px] transition-all relative flex flex-col justify-end h-full overflow-hidden">
                <div className="absolute inset-0 bg-black opacity-50"></div>
                <Link href="/podcasts" className="stretched-link relative z-10">
                  <h3 className="text-[32px]">
                    Découvrir d&apos;autres podcasts
                  </h3>
                </Link>
              </div>
            </CarouselItem>
            <CarouselItem className="md:basis-[5%] lg:basis-[2%] mx-0 scale-125 max-md:basis-[0%] max-md:invisible">
              {isEnd &&
                <CarouselNext />
              }
            </CarouselItem>
          </CarouselContent>
          <SliderIndicator isStart={isStart} isEnd={isEnd} />
        </div>
      </div>
    </Carousel>
  )
}